import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { logoutUser } from "../api/auth";
import { useGuardStore } from "../context/GuardContext";

export default function GuardNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedGuard } = useGuardStore();

  const [showNavbar, setShowNavbar] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 991 : false
  );
  const [leftMenuOpen, setLeftMenuOpen] = useState(false);
  const [userDropOpen, setUserDropOpen] = useState(false);

  const leftMenuRef = useRef(null);
  const userRef = useRef(null);

  const guardName = selectedGuard?.name || "Guard";
  const guardEmail = selectedGuard?.email || "guard@gmail.com";

  /* RESPONSIVE WIDTH CONTROL */
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 991;
      setShowNavbar(isMobile);

      if (!isMobile) {
        setLeftMenuOpen(false);
        setUserDropOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  /* CLOSE DROPDOWNS ON ROUTE CHANGE */
  useEffect(() => {
    setLeftMenuOpen(false);
    setUserDropOpen(false);
  }, [location.pathname]);

  /* OUTSIDE CLICK CLOSE */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (leftMenuRef.current && !leftMenuRef.current.contains(e.target)) {
        setLeftMenuOpen(false);
      }
      if (userRef.current && !userRef.current.contains(e.target)) {
        setUserDropOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  /* LOGOUT */
  const handleLogout = () => {
    logoutUser();
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  };

  /* HIDE ON DESKTOP */
  if (!showNavbar) return null;

  return (
    <>
      <nav className="guard-mobile-navbar">
        {/* LEFT: MENU + BRAND */}
        <div className="nav-left" ref={leftMenuRef}>
          <button
            className={`hamburger-btn ${leftMenuOpen ? "active" : ""}`}
            aria-label="Toggle menu"
            onClick={() => {
              setLeftMenuOpen((p) => !p);
              setUserDropOpen(false);
            }}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          <div className="brand-logo">
            <span>Guard Panel</span>
          </div>

          {leftMenuOpen && (
            <div className="mobile-menu-dropdown">
              <NavLink to="/guarddashboard" className="menu-link">
                <i className="fas fa-tachometer-alt"></i>
                Dashboard
              </NavLink>
              <NavLink to="/guardNotification" className="menu-link">
                <i className="fas fa-bell"></i>
                Notifications
              </NavLink>

              <NavLink to="/guardshift" className="menu-link">
                <i className="fas fa-briefcase"></i>
                My Shift
              </NavLink>


              <NavLink to="/guardhistory" className="menu-link">
                <i className="fas fa-history"></i>
                Duty History
              </NavLink>


              <NavLink to="/guardIncident" className="menu-link">
                <i className="fas fa-exclamation-triangle"></i>
                Incident
              </NavLink>

              <NavLink to="/guardIncidentHistory" className="menu-link">
                <i className="fas fa-clipboard-list"></i>
                Incident History
              </NavLink>
              <NavLink to="/guardprofile" className="menu-link">
                <i className="fas fa-user"></i>
                My Profile
              </NavLink>
            </div>
          )}
        </div>

        {/* RIGHT: PROFILE */}
        <div className="nav-right" ref={userRef}>
          <button
            className="profile-btn"
            aria-label="Open profile menu"
            onClick={() => {
              setUserDropOpen((p) => !p);
              setLeftMenuOpen(false);
            }}
          >
            <img
              src="/assets/img/avtar.jpg"
              alt="Guard"
              className="profile-avatar"
            />
          </button>

          {userDropOpen && (
            <div className="profile-dropdown">
              <div className="profile-header">
                <img
                  src="/assets/img/avtar.jpg"
                  alt="Guard"
                  className="profile-large-avatar"
                />
                <div className="profile-info">
                  <strong className="profile-name">{guardName}</strong>
                  <span className="profile-email">{guardEmail}</span>
                </div>
              </div>

              <NavLink to="/guardprofile" className="profile-link">
                <i className="fas fa-user-cog"></i>
                My Profile
              </NavLink>

              <button onClick={handleLogout} className="logout-button">
                <i className="fas fa-sign-out-alt"></i>
                Logout
              </button>
            </div>
          )}
        </div>
      </nav>

      <style jsx>{`
        .guard-mobile-navbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.9rem 1.2rem;
          background: rgba(255, 255, 255, 0.92);
          backdrop-filter: blur(16px);
          border-bottom: 1px solid rgba(59, 130, 246, 0.3);
          position: sticky;
          top: 0;
          z-index: 1000;
          width: 100%;
          box-shadow: 0 4px 20px rgba(30, 64, 175, 0.15);
        }

        /* LEFT SECTION */
        .nav-left {
          display: flex;
          align-items: center;
          gap: 1rem;
          position: relative;
        }

        .hamburger-btn {
          width: 44px;
          height: 44px;
          background: linear-gradient(135deg, #3b82f6, #1e40af);
          border: none;
          border-radius: 14px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 5px;
          cursor: pointer;
          transition: all 0.4s ease;
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
        }

        .hamburger-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 30px rgba(59, 130, 246, 0.5);
        }

        .hamburger-btn span {
          width: 22px;
          height: 3px;
          background: white;
          border-radius: 3px;
          transition: all 0.4s ease;
        }

        .hamburger-btn.active span:nth-child(1) {
          transform: rotate(45deg) translate(6px, 6px);
        }

        .hamburger-btn.active span:nth-child(2) {
          opacity: 0;
        }

        .hamburger-btn.active span:nth-child(3) {
          transform: rotate(-45deg) translate(6px, -6px);
        }

        .brand-logo {
          font-size: 1.4rem;
          font-weight: 800;
          color: #1e40af;
          letter-spacing: 0.5px;
          white-space: nowrap;
        }

        .mobile-menu-dropdown {
          position: absolute;
          top: 68px;
          left: 0;
          width: 260px;
          background: rgba(255, 255, 255, 0.96);
          backdrop-filter: blur(16px);
          border-radius: 20px;
          box-shadow: 0 15px 40px rgba(59, 130, 246, 0.3);
          border: 1px solid rgba(59, 130, 246, 0.4);
          overflow: hidden;
          z-index: 1000;
          animation: slideDown 0.4s ease-out;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .menu-link {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.1rem 1.5rem;
          text-decoration: none;
          color: #334155;
          font-weight: 600;
          font-size: 1.05rem;
          transition: all 0.3s ease;
          border-bottom: 1px solid rgba(59, 130, 246, 0.1);
        }

        .menu-link:hover {
          background: rgba(59, 130, 246, 0.12);
          color: #1e40af;
          padding-left: 2rem;
        }

        .menu-link i {
          font-size: 1.2rem;
          color: #3b82f6;
          width: 24px;
          text-align: center;
        }

        /* RIGHT SECTION */
        .nav-right {
          position: relative;
        }

        .profile-btn {
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
        }

        .profile-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid #3b82f6;
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
          transition: all 0.3s ease;
        }

        .profile-btn:hover .profile-avatar {
          transform: scale(1.1);
          box-shadow: 0 10px 30px rgba(59, 130, 246, 0.5);
        }

        .profile-dropdown {
          position: absolute;
          right: 0;
          top: 68px;
          width: 280px;
          max-width: 90vw;
          background: rgba(255, 255, 255, 0.96);
          backdrop-filter: blur(16px);
          border-radius: 20px;
          box-shadow: 0 15px 40px rgba(59, 130, 246, 0.3);
          border: 1px solid rgba(59, 130, 246, 0.4);
          overflow: hidden;
          z-index: 1000;
          animation: slideDown 0.4s ease-out;
        }

        .profile-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.5rem;
          background: linear-gradient(135deg, #f0f7ff, #e0e7ff);
          border-bottom: 1px solid rgba(59, 130, 246, 0.2);
        }

        .profile-large-avatar {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid #3b82f6;
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.3);
        }

        .profile-info {
          flex: 1;
        }

        .profile-name {
          display: block;
          font-size: 1.3rem;
          font-weight: 700;
          color: #1e3a8a;
        }

        .profile-email {
          display: block;
          font-size: 0.95rem;
          color: #64748b;
          margin-top: 0.3rem;
        }

        .profile-link {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.1rem 1.5rem;
          text-decoration: none;
          color: #334155;
          font-weight: 600;
          font-size: 1.05rem;
          transition: all 0.3s ease;
        }

        .profile-link:hover {
          background: rgba(59, 130, 246, 0.12);
          color: #1e40af;
          padding-left: 2rem;
        }

        .profile-link i {
          font-size: 1.2rem;
          color: #3b82f6;
          width: 24px;
          text-align: center;
        }

        .logout-button {
          width: 100%;
          padding: 1.2rem;
          background: linear-gradient(135deg, #dc2626, #b91c1c);
          color: white;
          border: none;
          font-size: 1.1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.8rem;
        }

        .logout-button:hover {
          background: linear-gradient(135deg, #b91c1c, #991b1b);
          transform: translateY(-2px);
        }

        /* Responsive Adjustments */
        @media (max-width: 480px) {
          .guard-mobile-navbar {
            padding: 0.8rem 1rem;
          }

          .brand-logo span {
            font-size: 1.2rem;
          }

          .hamburger-btn {
            width: 40px;
            height: 40px;
            gap: 4px;
          }

          .hamburger-btn span {
            width: 20px;
            height: 2.5px;
          }

          .profile-avatar {
            width: 42px;
            height: 42px;
          }

          .mobile-menu-dropdown,
          .profile-dropdown {
            width: 100%;
            left: 0;
            right: 0;
            margin: 0 1rem;
          }
        }
      `}</style>
    </>
  );
}

// import React, { useState, useEffect, useRef } from "react";
// import { NavLink, useNavigate, useLocation } from "react-router-dom";
// import { logoutUser } from "../api/auth";
// import { useGuardStore } from "../context/GuardContext";

// export default function GuardNavbar() {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const { selectedGuard } = useGuardStore();

//   const [showNavbar, setShowNavbar] = useState(
//     typeof window !== "undefined" ? window.innerWidth <= 991 : false
//   );
//   const [leftMenuOpen, setLeftMenuOpen] = useState(false);
//   const [userDropOpen, setUserDropOpen] = useState(false);

//   const leftMenuRef = useRef(null);
//   const userRef = useRef(null);

//   const guardName = selectedGuard?.name || "Guard";
//   const guardEmail = selectedGuard?.email || "guard@gmail.com";

//   /*  RESPONSIVE WIDTH CONTROL */
//   useEffect(() => {
//     const handleResize = () => {
//       const isMobile = window.innerWidth <= 991;
//       setShowNavbar(isMobile);

//       if (!isMobile) {
//         setLeftMenuOpen(false);
//         setUserDropOpen(false);
//       }
//     };

//     handleResize();
//     window.addEventListener("resize", handleResize);
//     window.addEventListener("orientationchange", handleResize);

//     return () => {
//       window.removeEventListener("resize", handleResize);
//       window.removeEventListener("orientationchange", handleResize);
//     };
//   }, []);

//   /*  CLOSE DROPDOWNS ON ROUTE CHANGE */
//   useEffect(() => {
//     setLeftMenuOpen(false);
//     setUserDropOpen(false);
//   }, [location.pathname]);

//   /*  OUTSIDE CLICK CLOSE */
//   useEffect(() => {
//     const handleClickOutside = (e) => {
//       if (leftMenuRef.current && !leftMenuRef.current.contains(e.target)) {
//         setLeftMenuOpen(false);
//       }
//       if (userRef.current && !userRef.current.contains(e.target)) {
//         setUserDropOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     document.addEventListener("touchstart", handleClickOutside);

//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//       document.removeEventListener("touchstart", handleClickOutside);
//     };
//   }, []);

//   /*  LOGOUT */
//   const handleLogout = () => {
//     logoutUser();
//     localStorage.clear();
//     sessionStorage.clear();
//     navigate("/login");
//   };

//   /*  HIDE ON DESKTOP */
//   if (!showNavbar) return null;

//   return (
//     <nav style={styles.navbar}>
//       {/*  LEFT SIDE : MENU + BRAND */}
//       <div style={styles.leftBox} ref={leftMenuRef}>
//         <button
//           style={styles.menuBtn}
//           aria-label="Open menu"
//           onClick={() => {
//             setLeftMenuOpen((p) => !p);
//             setUserDropOpen(false);
//           }}
//         >
//           ☰
//         </button>

//         <span style={styles.brand}>Guard Panel</span>

//         {leftMenuOpen && (
//           <div style={styles.leftDropdown}>
//             <NavLink to="/guarddashboard" style={styles.menuItem}>
//               Dashboard
//             </NavLink>

//             <NavLink to="/guardshift" style={styles.menuItem}>
//               My Shift
//             </NavLink>

//             <NavLink to="/guardprofile" style={styles.menuItem}>
//               My Profile
//             </NavLink>

//             <NavLink to="/guardhistory" style={styles.menuItem}>
//               Duty History
//             </NavLink>

//             <NavLink to="/guardNotification" style={styles.menuItem}>
//               Notifications
//             </NavLink>

//             <NavLink to="/guardIncident" style={styles.menuItem}>
//               Incident
//             </NavLink>

//             <NavLink to="/guardIncidentHistory" style={styles.menuItem}>
//               Incident History
//             </NavLink>
//           </div>
//         )}
//       </div>

//       {/*  RIGHT SIDE : PROFILE ICON */}
//       <div style={styles.userBox} ref={userRef}>
//         <button
//           style={styles.profileIconBtn}
//           aria-label="Open profile"
//           onClick={() => {
//             setUserDropOpen((p) => !p);
//             setLeftMenuOpen(false);
//           }}
//         >
//           <img
//             src="/assets/img/avtar.jpg"
//             alt="Guard"
//             style={styles.avatar}
//           />
//         </button>

//         {userDropOpen && (
//           <div style={styles.userDropdown}>
//             <div style={styles.userHeader}>
//               <img
//                 src="/assets/img/avtar.jpg"
//                 alt="Guard"
//                 style={styles.userBigAvatar}
//               />
//               <div>
//                 <strong>{guardName}</strong>
//                 <br />
//                 <small>{guardEmail}</small>
//               </div>
//             </div>

//             <NavLink to="/guardprofile" style={styles.userItem}>
//               My Profile
//             </NavLink>

//             <button onClick={handleLogout} style={styles.logoutBtn}>
//               Logout
//             </button>
//           </div>
//         )}
//       </div>
//     </nav>
//   );
// }

// /* ===============================
//     GUARD NAVBAR STYLES (RESPONSIVE)
// ================================ */

// const styles = {
//   navbar: {
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "space-between",
//     padding: "10px 14px",
//     background: "#ffffff",
//     borderBottom: "1px solid #ddd",
//     position: "sticky",
//     top: 0,
//     zIndex: 1000,
//     width: "100%",
//   },

//   /* LEFT */
//   leftBox: {
//     display: "flex",
//     alignItems: "center",
//     gap: 10,
//     position: "relative",
//     maxWidth: "70%",
//     flexWrap: "nowrap",
//   },

//   menuBtn: {
//     fontSize: 20,
//     fontWeight: "900",
//     background: "none",
//     border: "2px solid #4e54c8",
//     borderRadius: 6,
//     padding: "2px 8px",
//     cursor: "pointer",
//     color: "#4e54c8",
//     minWidth: 36,
//     minHeight: 32,
//   },

//   brand: {
//     fontSize: 16,
//     fontWeight: "700",
//     color: "#4e54c8",
//     whiteSpace: "nowrap",
//   },

//   leftDropdown: {
//     position: "absolute",
//     top: 38,
//     left: 0,
//     width: 240,
//     maxHeight: "70vh",
//     overflowY: "auto",
//     background: "#fff",
//     borderRadius: 12,
//     boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
//     zIndex: 1000,
//   },

//   menuItem: {
//     display: "block",
//     padding: "12px 14px",
//     textDecoration: "none",
//     color: "#333",
//     borderBottom: "1px solid #eee",
//     fontWeight: 600,
//     fontSize: 14,
//   },

//   /* RIGHT */
//   userBox: {
//     position: "relative",
//     flexShrink: 0,
//   },

//   profileIconBtn: {
//     background: "none",
//     border: "none",
//     padding: 0,
//     cursor: "pointer",
//   },

//   avatar: {
//     width: 34,
//     height: 34,
//     borderRadius: "50%",
//     border: "2px solid #4e54c8",
//     objectFit: "cover",
//   },

//   userDropdown: {
//     position: "absolute",
//     right: 0,
//     top: 42,
//     width: 220,
//     maxWidth: "90vw",
//     background: "#fff",
//     borderRadius: 12,
//     boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
//     overflow: "hidden",
//     zIndex: 1000,
//   },

//   userHeader: {
//     display: "flex",
//     gap: 10,
//     padding: 12,
//     background: "#f8f9fa",
//     borderBottom: "1px solid #eee",
//     alignItems: "center",
//   },

//   userBigAvatar: {
//     width: 38,
//     height: 38,
//     borderRadius: "50%",
//   },

//   userItem: {
//     display: "block",
//     padding: "10px 14px",
//     textDecoration: "none",
//     color: "#333",
//     fontWeight: 600,
//     fontSize: 14,
//   },

//   logoutBtn: {
//     width: "100%",
//     border: "none",
//     background: "#dc3545",
//     color: "#fff",
//     padding: 12,
//     fontWeight: 700,
//     cursor: "pointer",
//     fontSize: 14,
//   },
// };
